import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';

export default function SectionGallery() {
  const { ref: headRef, isVisible: headVisible } = useInView();
  const { ref: gridRef, isVisible: gridVisible } = useInView({ threshold: 0.05 });

  return (
    <section className="bg-rdr-cream py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-14 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div>
            <div className="inline-flex items-center gap-4 mb-5">
              <span className="gold-divider" />
              <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">The Ranch</p>
            </div>
            <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl leading-tight max-w-xl">
              A Setting That Speaks for Itself
            </h2>
          </div>
          <Link
            to="/location#contact"
            className="flex-shrink-0 self-start lg:self-auto inline-block bg-rdr-charcoal text-white font-sans text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-rdr-warm transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Schedule a Private Tour
          </Link>
        </div>

        {/* Gallery Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-12 grid-rows-2 gap-3 h-[600px] lg:h-[700px]"
        >
          {/* Large featured — left */}
          <div className={`col-span-12 lg:col-span-7 row-span-2 img-hover-zoom overflow-hidden relative scale-in ${gridVisible ? 'is-visible' : ''}`}>
            <img
              src="https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/4af24bd9-4fc6-4d89-abe7-e71420a4fc3d/4I6A0440.jpg"
              alt="Luxury wedding ceremony at Red Door Ranch hilltop estate Joshua TX"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="text-white/80 font-sans text-xs tracking-[0.2em] uppercase">Wedding Ceremony</p>
            </div>
          </div>

          {/* Top right */}
          <div className={`col-span-12 lg:col-span-5 row-span-1 img-hover-zoom overflow-hidden relative fade-up delay-200 ${gridVisible ? 'is-visible' : ''}`}>
            <img
              src="https://public.readdy.ai/ai/img_res/c74501a1de64e8b3345f121745f6947c.jpg"
              alt="Elegant wedding reception hall setup at Red Door Ranch"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark/30 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-5">
              <p className="text-white/80 font-sans text-xs tracking-[0.2em] uppercase">Reception Hall</p>
            </div>
          </div>

          {/* Bottom right */}
          <div className={`col-span-12 lg:col-span-5 row-span-1 img-hover-zoom overflow-hidden relative fade-up delay-400 ${gridVisible ? 'is-visible' : ''}`}>
            <img
              src="https://readdy.ai/api/search-image?query=beautiful-outdoor-covered-patio-wedding-venue-with-string-lights-draped-fabric-wooden-beams-lush-green-landscaping-golden-sunset-glow-elegant-ranch-style-outdoor-event-space-texas-hill-country-warm-amber-light&width=800&height=400&seq=gallery-patio-01&orientation=landscape"
              alt="Covered outdoor patio event space at Red Door Ranch"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark/30 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-5">
              <p className="text-white/80 font-sans text-xs tracking-[0.2em] uppercase">Covered Patio · 3,500 sq ft</p>
            </div>
          </div>
        </div>

        {/* Second row — 3 images */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 mt-3`}>
          {[
            {
              src: 'https://readdy.ai/api/search-image?query=luxurious-bridal-suite-dressing-room-with-vanity-mirror-with-warm-bulb-lights-plush-velvet-seating-white-wedding-dress-hanging-elegant-romantic-cream-and-gold-decor-soft-natural-window-light&width=600&height=350&seq=gallery-bridal-01&orientation=landscape',
              alt: 'Bridal suite at Red Door Ranch venue Joshua TX',
              label: 'Bridal Suite',
              delay: 'delay-100',
            },
            {
              src: 'https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/1631653889244-4L86Y1JQ4I8PA1YERL3S/FA606739-06AC-4820-A84F-631482186EA5.jpeg',
              alt: 'Private event setup at Red Door Ranch luxury venue',
              label: 'Private Events',
              delay: 'delay-300',
            },
            {
              src: 'https://readdy.ai/api/search-image?query=sweeping-panoramic-hilltop-view-from-luxury-texas-wedding-venue-with-rolling-green-hills-golden-hour-light-sunset-sky-warm-amber-glow-open-countryside-dramatic-landscape-photography-elevated-scenic-overlook&width=600&height=350&seq=gallery-view-01&orientation=landscape',
              alt: 'Hilltop scenic views from Red Door Ranch estate',
              label: '20-Acre Hilltop Views',
              delay: 'delay-500',
            },
          ].map((img, i) => (
            <div
              key={i}
              className={`img-hover-zoom overflow-hidden relative h-52 fade-up ${img.delay} ${gridVisible ? 'is-visible' : ''}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5">
                <p className="text-white/80 font-sans text-xs tracking-[0.2em] uppercase">{img.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
